import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { GetMyCart, CartDelete } from "../../api/cartService";
import { insertEnquiry } from "../../api/enquiryService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCount } from "../../Context/CountContext";
import "../../assets/css/Addtocart.css";
import EnquiryModal from "../Enquiry/EnquiryModal";
import CommonPopUpModal from "../../components/Common/CommonPopUpModal";
import CommonToastContainer from "../../components/Common/CommonToastContainer";
import { motion, AnimatePresence } from "framer-motion";
import FullScreenLoader from "../../components/Common/Loader";
import PriceValue from "../../components/Common/PriceValue";

const DEFAULT_IMAGE = "/assets/images/noImage/no-image3.png";

const AddToCart = () => {
	document.title = "My Cart";

	const navigate = useNavigate();
	const { fetchCartCount, fetchEnquiryCount } = useCount();
	const [cartItems, setCartItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
	const [enquiryItems, setEnquiryItems] = useState([]);
	const [enquiryRemark, setEnquiryRemark] = useState("");
	const [submittingEnquiry, setSubmittingEnquiry] = useState(false);
	const [isCommonModalOpen, setIsCommonModalOpen] = useState(false);
	const [commonModalMessage, setCommonModalMessage] = useState("");
	const [commonModalPageType, setCommonModalPageType] = useState("");
	const [commonModalTitle, setCommonModalTitle] = useState("");
	const [loadedImages, setLoadedImages] = useState({});
	const [toasts, setToasts] = useState([]);
	const [copied, setCopied] = useState(false);
	const [deletePayload, setDeletePayload] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [showLoader, setShowLoader] = useState(false);
	const [progress, setProgress] = useState(0);
	const [dataReady, setDataReady] = useState(false);

	const showToast = (toastData) => {
		setToasts(prev => [...prev, { id: Date.now(), ...toastData }]);
	};

	const removeToast = (id) => {
		setToasts(prev => prev.filter(t => t.id !== id));
	};

	const showVal = (val) =>
		val === 0 || val === "0" || val === null || val === undefined ? "-" : val;

	useEffect(() => {
		fetchCartItems();
	}, []);

	const fetchCartItems = async () => {
		setLoading(true);
		setShowLoader(true);
		setDataReady(false);
		try {
			const response = await GetMyCart();
			const rows = (response?.CartItems || []).map(item => ({
				...item,
				selected: false,
			}));
			setCartItems(rows);
		} catch (e) {
			console.error(e);
		} finally {
			setLoading(false);
			setProgress(100);
			setTimeout(() => {
				setShowLoader(false);
				setDataReady(true);
			}, 400);
		}
	};

	useEffect(() => {
		if (!showLoader) return;
		let value = 10;
		setProgress(value);
		const interval = setInterval(() => {
			value = Math.min(value + Math.random() * 15, 90);
			setProgress(value);
		}, 400);
		return () => clearInterval(interval);
	}, [showLoader]);

	const formatDateKeepTime = (dateTime) => {
		if (!dateTime) return "-";

		const date = parseISO(dateTime);
		return format(date, "dd MMM yyyy hh:mm a");
	};

	const isInvalid = (val) => val === null || val === undefined || val === "" || Number(val) === 0;

	const formatPcsCts = (pcs, wt) => isInvalid(pcs) || isInvalid(wt) ? "-" : `${pcs} Pcs / ${wt} Cts`;

	const toggleSelect = (cartId) => {
		setCartItems(prev =>
			prev.map(item =>
				item.Cart_ID === cartId
					? { ...item, selected: !item.selected }
					: item
			)
		);
	};

	const handleSelectAll = () => {
		const allSelected =
			cartItems.length > 0 && cartItems.every(item => item.selected);
		setCartItems(prev =>
			prev.map(item => ({ ...item, selected: !allSelected }))
		);
	};

	const openDeleteModal = (item) => {
		setDeletePayload({
			Cart_ID: item.Cart_ID,
			Style_ID: item.Style_ID,
		});

		setCommonModalTitle("Remove Item");
		setCommonModalMessage("Are you sure you want to remove this item?");
		setCommonModalPageType("delete");
		setIsCommonModalOpen(true);
	};

	const confirmDelete = async () => {
		if (!deletePayload) return;

		try {
			await CartDelete({
				Cart_ID: deletePayload.Cart_ID.toString(),
				Style_ID: deletePayload.Style_ID.toString(),
			});

			setCartItems(prev =>
				prev.filter(x => x.Cart_ID !== deletePayload.Cart_ID)
			);

			fetchCartCount();
			setIsCommonModalOpen(false);

			showToast({
				title: "Success!",
				message: <>Product removed from cart successfully.</>,
				type: "success",
			});
		} catch {
			toast.error("Failed to remove item");
			setIsCommonModalOpen(false);
		} finally {
			setDeletePayload(null);
		}
	};

	const selectedItems = cartItems.filter(item => item.selected);

	const handleEnquireNow = () => {
		if (!selectedItems.length) {
			toast.warning("Please select at least one product");
			return;
		}

		setEnquiryItems(
			selectedItems.map(item => ({
				Style_ID: item.Style_ID,
				StyleNo: item.StyleNo,
				JewelCode: item.JewelCode,
				Shortdescription: item.Shortdescription,
				MetalQuality: item.MetalQuality,
				MetalTone: item.MetalTone,
				TotalNetWt: item.TotalNetWt,
				Cart_ID: item.Cart_ID,
				Qunatity: 1,
			}))
		);
		setIsEnquiryOpen(true);
	};

	const removeEnquiryItem = (productId) => {
		setEnquiryItems(prev =>
			prev.filter(p => p.Style_ID !== productId)
		);
	};

	const resetEnquiryState = () => {
		setEnquiryItems([]);
		setEnquiryRemark("");
		setIsEnquiryOpen(false);
	};

	const copyEnquiryNo = () => {
		if (!isValidEnquiryNo(commonModalTitle)) return;

		navigator.clipboard.writeText(commonModalTitle).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	};

	const submitEnquiry = async () => {
		if (!enquiryItems.length) return;

		setSubmittingEnquiry(true);

		const payload = {
			Enquiry_ID: "0",
			EnquiryNo: "",
			Status: "PENDING",
			StatusComment: enquiryRemark,
			StyleCount: String(enquiryItems.length),
			Styles: enquiryItems.map(x => ({
				Style_ID: x.Style_ID,
				StyleNo: x.StyleNo,
				JewelCode: x.JewelCode,
				Cart_ID: x.Cart_ID.toString() ?? "",
			}))
		};
		try {
			const res = await insertEnquiry(payload);				
			const messageObj = res?.EnquiryData?.[0];
			const rawMsg = messageObj?.ReturnMessage || "";
			console.log("Raw Message", rawMsg)
			const normalizedMsg = rawMsg.replace(/\s+/g, " ").trim();
			const enquiryNoMatch = normalizedMsg.match(/Enquiry\s*No\s*=\s*([A-Z0-9/]+)/i);

			const enquiryNo = enquiryNoMatch ? enquiryNoMatch[1] : "Enquiry Created";

			console.log("enquiryNo", enquiryNo)	

			const cleanMessage = normalizedMsg
				.replace(/^\(\d+\)\s*/g, "")
				.replace(/\(Enquiry\s*No\s*=.*?\)/i, "")
				.trim();

			setIsEnquiryOpen(false);
			fetchCartCount();
			fetchEnquiryCount();
			fetchCartItems();
			setCommonModalTitle(enquiryNo);
			setCommonModalMessage(cleanMessage);
			setCommonModalPageType("enquiry");
			setIsCommonModalOpen(true);
		} catch (error) {
			console.error("Exception inserting enquiry:", error);
			const ERRORMessage = error?.response?.data?.Message?.[0]?.ReturnMessage || error?.message || "Something went wrong";
			setCommonModalTitle("Error");
			setCommonModalMessage(ERRORMessage);
			setCommonModalPageType("error");
			setIsCommonModalOpen(true);
		} finally {
			setSubmittingEnquiry(false);
			resetEnquiryState();
		}
	};

	const handleImageLoad = (id) => {
		setLoadedImages(prev => ({ ...prev, [id]: true }));
	};

	const listVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.08,
			},
		},
	};

	const itemVariants = {
		hidden: {
			opacity: 0,
			y: 20,
		},
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.45,
				ease: "easeOut",
			},
		},
		exit: {
			opacity: 0,
			y: -15,
			transition: { duration: 0.3 },
		},
	};

	const isValidEnquiryNo = (val) => {
		if (!val) return false;
		return /^[A-Z]{1,5}\/\d{1,4}\/\d{1,4}$/i.test(val);
	};

	return (
		<Container className="cart-container">
			{showLoader && <FullScreenLoader progress={progress} />}
			<div className="cart-header">
				<div className="cart-header-text">
					<h1 className="fw-bold text-primary m-0">My Cart</h1>
					<p>({cartItems.length})</p>
				</div>
				<Button
					color="primary"
					onClick={handleEnquireNow}
					disabled={selectedItems.length === 0}
				>
					Enquire Now ({selectedItems.length})
				</Button>
			</div>

			{cartItems.length > 0 && (
				<div className="select-all-row">
					<label className="custom-check">
						<input
							type="checkbox"
							checked={
								cartItems.length > 0 &&
								cartItems.every(item => item.selected)
							}
							onChange={handleSelectAll}
						/>
						<span className="checkmark"></span>
						<span className="check-label">Select All</span>
					</label>
				</div>
			)}

			{cartItems.length === 0 && !loading && (
				<div className="cart-empty-wrapper">
					<div className="cart-empty-state text-center">
						<img
							src="/assets/images/cart/emptyCart.png"
							alt="Empty Cart"
							className="cart-empty-image mb-3"
						/>

						<h5 className="fw-semibold mb-2">Your Cart is Empty</h5>

						<p className="text-muted mb-3">
							Looks like you haven’t added any products yet.
							Browse our vault access and add designs you like.
						</p>

						<Button
							color="primary"
							size="sm"
							className="no-products-button"
							onClick={() => navigate("/vault-access")}
						>
							Continue Shopping
						</Button>
					</div>
				</div>
			)}

			<AnimatePresence mode="popLayout">
				<div>
					{cartItems.map((item, index) => (
						<motion.div
							key={item.Style_ID}
							layout
							initial={{ opacity: 0, y: 24 }}
							animate={{
								opacity: 1,
								y: 0,
								x: item.selected ? 4 : 0,
							}}
							exit={{
								opacity: 0,
								y: -12,
							}}
							transition={{
								opacity: { duration: 0.2 },
								y: { duration: 0.25 },
								layout: {
									type: "spring",
									stiffness: 260,
									damping: 30,
									mass: 0.8,
								},
							}}
							className={`cart-item ${item.selected ? "cart-item-selected" : ""}`}
						>
							<Row>
								<Col md="2" className="d-md-none cart-actions delete-button-top">
									<button
										className="delete-btn"
										onClick={() => openDeleteModal(item)}
									>
										<i className="bx bx-trash"></i>
									</button>
								</Col>

								<Col xs="12" sm="3" md="4" lg="2" className="cart-image-col">
									<label className="custom-check custom-check-sm">
										<input
											type="checkbox"
											checked={item.selected}
											onChange={() => toggleSelect(item.Cart_ID)}
										/>
										<span className="checkmark"></span>
									</label>

									<div className="image-wrapper">
										{!loadedImages[item.Cart_ID] && (
											<div className="image-skeleton"></div>
										)}

										<img
											src={item.Image1 || DEFAULT_IMAGE}
											alt={item.StyleNo}
											className={`cart-image ${loadedImages[item.Cart_ID] ? "visible" : ""
												}`}
											loading="lazy"
											onLoad={() => handleImageLoad(item.Cart_ID)}
											onError={(e) => {
												e.target.onerror = null;
												e.target.src = DEFAULT_IMAGE;
												handleImageLoad(item.Cart_ID);
											}}
										/>
									</div>
								</Col>

								<Col xs="12" sm="9" md="6" lg="8">
									<div className="cart-title mb-1 row align-items-start">
										<span className="text-primary cart-desc col-12 col-md-6">
											{item.Shortdescription || "Product Detail"}
										</span>

										<span className="text-muted cart-date col-12 col-md-4 mt-1 mt-md-0">
											{formatDateKeepTime(item.CartEntryDate)}
										</span>
									</div>


									<div className="d-flex align-items-baseline row mb-1">
										<span className="fw-bold me-1 col-12 col-md-6">
											Product Code: {" "}
											<a
												href={`/vault-access-detail/${item.Style_ID}`}
												target="_blank"
												rel="noopener noreferrer"
												className="clickable-code login-hover-link fw-normal"
											>
												{showVal(item.StyleNo)} / {showVal(item.JewelCode)}
											</a>
										</span>

										<span className="col-12 col-md-4 mt-1 mt-md-0">
											<PriceValue
												lineAmount={item.LineAmount}
												lineDiscount={item.LineDiscount}
												priceRemark={item.PriceRemark}
											/>
										</span>

									</div>

									<Row className="cart-details-row">
										<Col md="6">
											<span className="text-primary cart-subtitle mt-1">
												<i className="fa fa-box-open fs-6 me-2"></i>Product Details
											</span>

											<p className="detail-row">
												<span className="detail-label">Gold Purity</span>
												<span className="detail-value">
													{showVal(item.MetalQuality)} / {showVal(item.MetalTone)}
												</span>
											</p>

											<p className="detail-row">
												<span className="detail-label">Gold Weight</span>
												<span className="detail-value">
													{showVal(item.TotalNetWt)}
												</span>
											</p>

											<p className="detail-row">
												<span className="detail-label">Dimension</span>
												<span className="detail-value">
													{showVal(item.ItemSize)}
												</span>
											</p>
										</Col>

										<Col md="6">
											<span className="text-primary cart-subtitle">
												<i className="fa fa-gem fs-6 me-2"></i>Diamond Details
											</span>

											<p className="detail-row">
												<span className="detail-label">Diamond Weight</span>
												<span className="detail-value">
													{formatPcsCts(item.DiaPcs, item.DiaWt)}
												</span>
											</p>

											<p className="detail-row">
												<span className="detail-label">Color Diamond</span>
												<span className="detail-value">
													{formatPcsCts(item.CDiaPcs, item.CDiaWt)}
												</span>
											</p>

											<p className="detail-row">
												<span className="detail-label">Gross Weight</span>
												<span className="detail-value">
													{isInvalid(item.GrossWt) ? "-" : item.GrossWt}
												</span>
											</p>
										</Col>
									</Row>
								</Col>

								<Col md="2" className="cart-actions delete-button-bottom">
									<button
										className="delete-btn"
										onClick={() => openDeleteModal(item)}
									>
										<i className="bx bx-trash"></i>
									</button>
								</Col>
								
							</Row>
						</motion.div>
					))}
				</div>
			</AnimatePresence>

			<EnquiryModal
				isOpen={isEnquiryOpen}
				toggle={resetEnquiryState}
				items={enquiryItems}
				remark={enquiryRemark}
				setRemark={setEnquiryRemark}
				onRemoveItem={removeEnquiryItem}
				onSubmit={submitEnquiry}
				submitting={submittingEnquiry}
				title="Product Enquiry"
			/>

			<CommonPopUpModal
				isOpen={isCommonModalOpen}
				onClose={() => {
					setIsCommonModalOpen(false);
					setCommonModalPageType("");
					setCommonModalTitle("");
					setCommonModalMessage("");
				}}
				onConfirm={
					commonModalPageType === "delete" ? confirmDelete : null
				}
				message={commonModalMessage}
				pageType={commonModalPageType}
				title={
					commonModalPageType === "enquiry" ? (
						<>
							<div className="popup-subtitle">
								Thanks for reaching out to us.
							</div>

							<div className="popup-enquiry-label">
								Your Enquiry No is
							</div>

							<div
								className={`popup-enquiry-no ${isValidEnquiryNo(commonModalTitle) ? "copyable" : ""
									}`}
								onClick={copyEnquiryNo}
								title={
									isValidEnquiryNo(commonModalTitle)
										? "Click to copy"
										: ""
								}
							>
								<span>{commonModalTitle}</span>

								{isValidEnquiryNo(commonModalTitle) && (
									<span className={`copy-wrapper ${copied ? "copied" : ""}`}>
										<i className="bx bx-copy copy-icon" />
										{copied && (
											<span className="copied-tooltip">
												Copied!
											</span>
										)}
									</span>
								)}
							</div>
						</>
					) : (
						commonModalTitle
					)
				}
			/>
			<ToastContainer />
			<CommonToastContainer
				toasts={toasts}
				removeToast={removeToast}
			/>
		</Container>
	);
};

export default AddToCart;
